import { useEffect, useMemo, useState } from "react";
import { Button, Select, Tooltip, message } from "antd";
import { Download, Maximize2 } from "lucide-react";
import { PDFViewer, pdf } from "@react-pdf/renderer";
import ResumeDocument from "../pdf/ResumeDocument.jsx";
import { TEMPLATES } from "../pdf/templates/index.js";

// @react-pdf rebuilds the PDF on every prop change. Debouncing the data
// keeps the preview snappy while the user is typing.
function useDebouncedValue(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

const templateOptions = Object.entries(TEMPLATES).map(([k, t]) => ({
  value: k,
  label: t.label,
}));

const sanitizeForFilename = (s) =>
  (s || "resume")
    .trim()
    .replace(/[^a-z0-9_\-\s]/gi, "")
    .replace(/\s+/g, "_")
    .slice(0, 60) || "resume";

export default function LivePreview({
  sections,
  sectionData,
  templateKey,
  onTemplateChange,
  onExpand,
  resumeMetaData,
  generatePdfBlob,
}) {
  const [downloading, setDownloading] = useState(false);

  const debouncedSections = useDebouncedValue(sections, 800);
  const debouncedSectionData = useDebouncedValue(sectionData, 800);

  const authorName =
    resumeMetaData?.resumeFileName || sectionData?.["personalInfo"]?.fullName;

  const previewDoc = useMemo(
    () => (
      <ResumeDocument
        sections={debouncedSections}
        sectionData={debouncedSectionData}
        templateKey={templateKey}
        authorName={authorName}
      />
    ),
    [debouncedSections, debouncedSectionData, templateKey, authorName],
  );

  const handleDownload = async () => {
    try {
      setDownloading(true);
      // Use the live (non-debounced) data for the actual download so the
      // file always matches the most recent edits.

      // const liveDoc = (
      //   <ResumeDocument
      //     sections={sections}
      //     sectionData={sectionData}
      //     templateKey={templateKey}
      //     authorName={authorName}
      //   />
      // );
      // const blob = await pdf(liveDoc).toBlob();

      const blob = await generatePdfBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${sanitizeForFilename(authorName)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("PDF download failed", err);
      message.error("Could not generate PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex flex-col bg-[#e9ecef] min-h-0 h-full lg:h-[90vh] rounded-[6px]">
      <div className="px-3 py-2 bg-white border-b flex items-center gap-2 shrink-0">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-[#475467]">
          Live preview
        </span>
        <Select
          size="small"
          value={templateKey}
          onChange={onTemplateChange}
          options={templateOptions}
          style={{ width: 120 }}
        />
        <div className="ml-auto flex items-center gap-1">
          {onExpand ? (
            <Tooltip title="Open full preview">
              <Button
                size="small"
                type="text"
                icon={<Maximize2 size={14} />}
                onClick={onExpand}
              />
            </Tooltip>
          ) : null}
          <button
            onClick={handleDownload}
            loading={downloading}
            className="btn-orange py-[.3rem] text-[.85rem] px-4 rounded-[6px] flex gap-2 items-center mr-1"
          >
            <Download size={14} /> Download
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 h-full">
        <PDFViewer
          key={templateKey}
          style={{ width: "100%", height: "100%", border: "none" }}
          showToolbar={false}
        >
          {previewDoc}
        </PDFViewer>
      </div>
    </div>
  );
}
